import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AutoStories } from '@mui/icons-material';
import { MagnifyingGlassIcon, XIcon, ListBulletsIcon, UserPlusIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { font } from '../../utils/fontHelper';
import { glassStyles } from '../../tokens/designTokens';
import { getAppConfig, waitForAppConfig } from '@watchlist/shared';

export type CreateChoice = 'add-movie' | 'story' | 'list-solo' | 'list-collab' | 'import';

interface CreateBottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Called with the user's choice. The caller is expected to open the matching dialog. */
  onChoose: (choice: CreateChoice) => void;
}

interface Option {
  choice: CreateChoice;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

const OPTIONS: Option[] = [
  {
    choice: 'add-movie',
    icon: <MagnifyingGlassIcon size={22} />,
    title: 'Add movie',
    subtitle: 'Search for a movie to add to a list.',
  },
  {
    choice: 'story',
    icon: <AutoStories sx={{ fontSize: 22 }} />,
    title: 'Share to spotlight',
    subtitle: 'Feature a review or watchlist for 7 days.',
  },
  {
    choice: 'list-solo',
    icon: <ListBulletsIcon size={22} />,
    title: 'New list',
    subtitle: 'Start a personal list.',
  },
  {
    choice: 'list-collab',
    icon: <UserPlusIcon size={22} />,
    title: 'New collaborative list',
    subtitle: 'Invite crew members to build a list together.',
  },
  {
    choice: 'import',
    icon: <DownloadSimpleIcon size={22} />,
    title: 'Import',
    subtitle: 'Build a list from a screenshot, text, or link.',
  },
];

const CreateBottomSheet: React.FC<CreateBottomSheetProps> = ({ open, onClose, onChoose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const [importVisibility, setImportVisibility] = useState<'shown' | 'hidden' | 'disabled'>('disabled');

  useEffect(() => {
    waitForAppConfig().then(() => {
      setImportVisibility(getAppConfig().featureImportEnabled);
    });
  }, []);

  const visibleOptions = OPTIONS.filter(opt => opt.choice !== 'import' || importVisibility !== 'hidden');

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <BottomSheetDraggableHeader sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 18 }}>
          Create
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </BottomSheetDraggableHeader>

      <Box sx={{ px: 1, pb: 2 }}>
        {visibleOptions.map((opt) => {
          const isDisabled = opt.choice === 'import' && importVisibility === 'disabled';
          return (
          <Box
            key={opt.choice}
            component="button"
            disabled={isDisabled}
            onClick={isDisabled ? undefined : () => onChoose(opt.choice)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              px: 1.5,
              py: 1.5,
              borderRadius: 2,
              textAlign: 'left',
              color: isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
              opacity: isDisabled ? 0.5 : 1,
              borderBottom: `1px solid ${borderColor}`,
              '&:last-child': { borderBottom: 'none' },
              '&:hover': isDisabled ? {} : {
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              },
              '&:active': isDisabled ? {} : { opacity: 0.7 },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.primary,
                flexShrink: 0,
              }}
            >
              {opt.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: font(),
                  fontWeight: 600,
                  fontSize: 15,
                  lineHeight: 1.25,
                }}
              >
                {opt.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: font(),
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                  mt: 0.25,
                }}
              >
                {opt.subtitle}
              </Typography>
            </Box>
          </Box>
        );
        })}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <BottomSheet open={open} onClose={onClose} maxHeight="60dvh">
        {content}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...glassStyles.dialog(isDark), borderRadius: 3 } }}
    >
      {content}
    </Dialog>
  );
};

export default CreateBottomSheet;
